import { getLocalStorageItem } from 'base/localstorage';
import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import { FormInput } from 'base/ui/input';
import { Rows } from 'base/ui/layout';
import { menuDivider, menuItem } from 'base/ui/menu_model';
import { mount } from 'base/ui/overlay';
import { Textarea } from 'base/ui/textarea';
import { TextSmallLight } from 'base/ui/typography';
import { observable } from 'mobx';
import React, { useState } from 'react';
import { z } from 'zod';

const overrideStorageKey = 'promptOverrides';

const promptOverrideSchema = z.union([
  z.object({
    template: z.string(),
    model: z.string().optional(),
    temperature: z.number().optional(),
  }),
  z.string().transform(
    (
      v,
    ): {
      template: string;
      model?: string | undefined;
      temperature?: number | undefined;
    } => ({
      template: v,
    }),
  ),
]);
const promptOverridesSchema = z.record(promptOverrideSchema);

type PromptOverride = z.TypeOf<typeof promptOverrideSchema>;

class PromptOverridesStore {
  private overrides = observable.map<string, PromptOverride>();

  constructor() {
    this.loadFromLocalStorage();

    addEventListener('storage', (e) => {
      if (e.key === overrideStorageKey) {
        this.loadFromLocalStorage();
      }
    });
  }

  private loadFromLocalStorage() {
    const v = getLocalStorageItem(overrideStorageKey);
    const parsed = promptOverridesSchema.safeParse(v ? JSON.parse(v) : {});
    this.overrides.replace(parsed.success ? parsed.data : {});
  }

  private writeToLocalStorage() {
    localStorage.setItem(
      overrideStorageKey,
      JSON.stringify(Object.fromEntries(this.overrides.entries())),
    );
  }

  get(promptName: string): PromptOverride | undefined {
    return this.overrides.get(promptName);
  }

  set(promptName: string, value: PromptOverride) {
    this.overrides.set(promptName, value);
    this.writeToLocalStorage();
  }

  delete(promptName: string) {
    this.overrides.delete(promptName);
    this.writeToLocalStorage();
  }

  get size() {
    return this.overrides.size;
  }

  get entries() {
    return [...this.overrides.entries()];
  }
}
export const globalPromptOverridesStore = new PromptOverridesStore();

export const promptOverridesMenu = () => {
  return [
    ...(globalPromptOverridesStore.size > 0
      ? [
          ...globalPromptOverridesStore.entries.map(
            ([name, { template, model, temperature }]) => {
              return menuItem({
                label: name,
                onClick: () => {
                  const close = mount(
                    <PromptOverrideDialog
                      onClose={() => close()}
                      promptName={name}
                      promptTemplate={template}
                      promptModel={model}
                      promptTemperature={temperature}
                    />,
                  );
                },
              });
            },
          ),
          menuDivider(),
        ]
      : []),
    menuItem({
      label: 'Add…',
      onClick: () => {
        const close = mount(<PromptOverrideDialog onClose={() => close()} />);
      },
    }),
  ];
};

const PromptOverrideDialog = ({
  onClose,
  promptName: initialPromptName,
  promptTemplate: initialPromptTemplate,
  promptModel: initialPromptModel,
  promptTemperature: initialPromptTemperature,
}: {
  onClose: () => void;
  promptName?: string;
  promptTemplate?: string;
  promptModel?: string;
  promptTemperature?: number;
}) => {
  const [promptName, setPromptName] = useState(initialPromptName || '');
  const [promptTemplate, setPromptTemplate] = useState(
    initialPromptTemplate || '',
  );
  const [promptModel, setPromptModel] = useState(initialPromptModel || '');
  const [promptTemperature, setPromptTemperature] = useState(
    initialPromptTemperature !== undefined ? `${initialPromptTemperature}` : '',
  );

  const handleSave = () => {
    const temperatureValue = parseFloat(promptTemperature);

    globalPromptOverridesStore.set(promptName, {
      template: promptTemplate,
      model: promptModel || undefined,
      temperature: isNaN(temperatureValue) ? undefined : temperatureValue,
    });
    onClose();
  };

  const handleDelete = () => {
    if (!initialPromptName) {
      return;
    }
    globalPromptOverridesStore.delete(initialPromptName);
    onClose();
  };

  return (
    <Dialog
      title="Override prompt"
      onClose={onClose}
      buttons={
        <>
          {!!initialPromptName && (
            <Button
              variant="primary"
              background="negative"
              label="Delete"
              onClick={handleDelete}
            />
          )}
          <Button
            variant="primary"
            label="Save"
            onClick={handleSave}
            disabled={!promptName || !promptTemplate}
          />
        </>
      }
      width="wide"
    >
      <Rows spacing={16}>
        <FormInput
          required
          size="medium"
          type="text"
          value={promptName}
          onChange={setPromptName}
          placeholder="Prompt name"
          disabled={!!initialPromptName}
        />
        <FormInput
          size="medium"
          type="text"
          value={promptModel}
          onChange={setPromptModel}
          placeholder="Prompt model (optional)"
        />
        <FormInput
          size="medium"
          type="text"
          value={promptTemperature}
          onChange={(v) => setPromptTemperature(v)}
          placeholder="Prompt temperature (optional)"
        />
        <TextSmallLight>
          <Textarea
            allowNewlines
            value={promptTemplate}
            onChange={setPromptTemplate}
            placeholder="Prompt template"
          />
        </TextSmallLight>
      </Rows>
    </Dialog>
  );
};
